import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import { getBillingReport, getMonthlyBilling, clearBillingData } from "../../redux/reducers/billingSlice";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { Button, View, Text, Card, DateInput } from "../base";
import { Icon } from "../base/Icon";

// Utility functions
const formatDuration = (minutes: number): string => {
  if (minutes === 0) {
    return "0m";
  }
  if (minutes < 1) {
    return "< 1m";
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.floor(minutes % 60);
  if (hours === 0) {
    return `${remainingMinutes}m`;
  }
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const BillingCard = () => {
  const dispatch = useAppDispatch();
  const { currentReport, isLoading, error } = useAppSelector((state) => state.billing);
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();

  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDayOfMonth.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  });

  // Quick date range buttons
  const [selectedRange, setSelectedRange] = useState<string>("current-month");

  useEffect(() => {
    // Load current month by default
    handleQuickRange("current-month");

    return () => {
      dispatch(clearBillingData());
    };
  }, [dispatch]);

  const handleQuickRange = (range: string) => {
    const now = new Date();
    setSelectedRange(range);

    switch (range) {
      case "current-month":
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartDate(firstDay.toISOString().split("T")[0]);
        setEndDate(lastDay.toISOString().split("T")[0]);

        // Use monthly endpoint for current month
        dispatch(
          getMonthlyBilling({
            year: now.getFullYear().toString(),
            month: (now.getMonth() + 1).toString(),
            organizationId: effectiveOrganizationId,
          }),
        );
        break;

      case "last-month":
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        setStartDate(lastMonthStart.toISOString().split("T")[0]);
        setEndDate(lastMonthEnd.toISOString().split("T")[0]);

        dispatch(
          getMonthlyBilling({
            year: lastMonthStart.getFullYear().toString(),
            month: (lastMonthStart.getMonth() + 1).toString(),
            organizationId: effectiveOrganizationId,
          }),
        );
        break;

      case "last-7-days":
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        setStartDate(sevenDaysAgo.toISOString().split("T")[0]);
        setEndDate(now.toISOString().split("T")[0]);

        dispatch(
          getBillingReport({
            startDate: sevenDaysAgo.toISOString().split("T")[0],
            endDate: now.toISOString().split("T")[0],
            organizationId: effectiveOrganizationId,
          }),
        );
        break;

      case "last-30-days":
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        setStartDate(thirtyDaysAgo.toISOString().split("T")[0]);
        setEndDate(now.toISOString().split("T")[0]);

        dispatch(
          getBillingReport({
            startDate: thirtyDaysAgo.toISOString().split("T")[0],
            endDate: now.toISOString().split("T")[0],
            organizationId: effectiveOrganizationId,
          }),
        );
        break;
    }
  };

  const handleCustomDateRange = () => {
    if (startDate && endDate) {
      setSelectedRange("custom");
      dispatch(
        getBillingReport({
          startDate,
          endDate,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const quickRangeButtons = [
    { key: "current-month", label: "Current Month" },
    { key: "last-month", label: "Last Month" },
    { key: "last-7-days", label: "Last 7 Days" },
    { key: "last-30-days", label: "Last 30 Days" },
  ];

  return (
    <View className="space-y-6">
      {/* Header */}
      <View>
        <Text className="text-2xl font-bold text-gray-900 dark:text-white">Usage</Text>
      </View>

      {/* Date Range Controls */}
      <Card className="p-6">
        <View className="space-y-4">
          <Text className="text-lg font-semibold text-gray-900 dark:text-white">Select Time Period</Text>

          {/* Quick Range Buttons */}
          <View className="flex flex-wrap gap-2">
            {quickRangeButtons.map(({ key, label }) => (
              <Button
                key={key}
                variant={selectedRange === key ? "brand" : "secondary"}
                onClick={() => handleQuickRange(key)}
                className="text-sm"
              >
                {label}
              </Button>
            ))}
          </View>

          {/* Custom Date Range */}
          <View className="flex flex-col sm:flex-row sm:items-center gap-4">
            <View className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">From:</Text>
              <DateInput
                value={startDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                className="w-full sm:w-40"
              />
            </View>

            <View className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">To:</Text>
              <DateInput
                value={endDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                className="w-full sm:w-40"
              />
            </View>

            <Button
              onClick={handleCustomDateRange}
              variant="brand"
              isDisabled={!startDate || !endDate || isLoading}
              className="w-full sm:w-auto flex items-center gap-2"
            >
              <Icon
                name="search"
                size={16}
              />
              Apply
            </Button>
          </View>
        </View>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <View className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </View>
      )}

      {/* Error State */}
      {error && (
        <Card className="mb-6">
          <View className="p-6 text-center">
            <Icon
              name="info"
              size={48}
              className="text-red-500 mx-auto mb-4"
            />
            <Text className="text-lg font-semibold text-red-600 mb-2">Error Loading Billing Data</Text>
            <Text className="text-gray-600 dark:text-gray-400">{error}</Text>
          </View>
        </Card>
      )}

      {/* Usage Summary Cards */}
      {currentReport && !isLoading && (
        <>
          <View className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card>
              <View className="p-6">
                <View className="flex items-center justify-between">
                  <View className="flex flex-col gap-2">
                    <Text className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sessions</Text>
                    <Text className="text-2xl font-bold text-gray-900 dark:text-white">{currentReport.usage.totalSessions.toLocaleString()}</Text>
                  </View>
                  <Icon
                    name="phone"
                    size={24}
                    className="text-blue-500"
                  />
                </View>
              </View>
            </Card>

            <Card>
              <View className="p-6">
                <View className="flex items-center justify-between">
                  <View className="flex flex-col gap-2">
                    <Text className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Duration</Text>
                    <Text className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(currentReport.usage.totalDurationMinutes)}</Text>
                  </View>
                  <Icon
                    name="clock"
                    size={24}
                    className="text-green-500"
                  />
                </View>
              </View>
            </Card>

            <Card>
              <View className="p-6">
                <View className="flex items-center justify-between">
                  <View className="flex flex-col gap-2">
                    <Text className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Session Length</Text>
                    <Text className="text-2xl font-bold text-gray-900 dark:text-white">
                      {currentReport.usage.totalSessions > 0
                        ? formatDuration(currentReport.usage.totalDurationMinutes / currentReport.usage.totalSessions)
                        : "0m"}
                    </Text>
                  </View>
                  <Icon
                    name="trending-up"
                    size={24}
                    className="text-purple-500"
                  />
                </View>
              </View>
            </Card>
          </View>

          {/* Agent Breakdown Table */}
          <Card>
            <View className="p-6">
              <Text className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Detailed Breakdown by Agent</Text>

              {currentReport.usage.agentBreakdown && currentReport.usage.agentBreakdown.length > 0 ? (
                <View className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sessions</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Duration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Avg Session</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentReport.usage.agentBreakdown
                        .slice()
                        .sort((a, b) => b.durationMinutes - a.durationMinutes) // Sort by usage (highest first)
                        .map((agent, index) => {
                          const percentOfTotal =
                            currentReport.usage.totalDurationMinutes > 0 ? (agent.durationMinutes / currentReport.usage.totalDurationMinutes) * 100 : 0;
                          const avgSessionDuration = agent.sessionCount > 0 ? agent.durationMinutes / agent.sessionCount : 0;

                          return (
                            <tr
                              key={agent.agentId}
                              className={index % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50 dark:bg-gray-800"}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <View className="flex items-center">
                                  <View className="ml-3">
                                    <Text className="text-sm font-medium text-gray-900 dark:text-white">{agent.agentName}</Text>
                                  </View>
                                </View>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Text className="text-sm text-gray-900 dark:text-white font-medium">{agent.sessionCount.toLocaleString()}</Text>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Text className="text-sm text-gray-900 dark:text-white font-medium">{formatDuration(agent.durationMinutes)}</Text>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Text className="text-sm text-gray-500 dark:text-gray-400">{formatDuration(avgSessionDuration)}</Text>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <View className="flex items-center">
                                  <View className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                                    <View
                                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(percentOfTotal, 100)}%` }}
                                    />
                                  </View>
                                  <Text className="text-sm text-gray-500 dark:text-gray-400 min-w-[3rem]">{percentOfTotal.toFixed(1)}%</Text>
                                </View>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </View>
              ) : (
                <View className="flex flex-col items-center justify-center py-8 space-y-4">
                  <Text className="text-gray-900 dark:text-white text-center">No agent activity found for the selected time period.</Text>
                </View>
              )}
            </View>
          </Card>
        </>
      )}
    </View>
  );
};
